/**
 * Product list vs selling price — shared by admin + storefront.
 * API may use originalPrice, compareAtPrice, mrp, listPrice, etc.
 */

export function parsePriceInput(value) {
  if (value === undefined || value === null || value === '') return null;
  const n = parseFloat(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** List / real / MRP from any common backend field. */
export function getListPriceFromProduct(product) {
  if (!product) return null;
  return (
    parsePriceInput(product.originalPrice) ??
    parsePriceInput(product.original_price) ??
    parsePriceInput(product.compareAtPrice) ??
    parsePriceInput(product.compare_at_price) ??
    parsePriceInput(product.listPrice) ??
    parsePriceInput(product.list_price) ??
    parsePriceInput(product.mrp) ??
    parsePriceInput(product.regularPrice) ??
    parsePriceInput(product.regular_price) ??
    null
  );
}

/** Storefront + cards: selling price + list price for strikethrough. */
export function normalizeProductPricesForDisplay(product) {
  if (!product) {
    return { price: 0, originalPrice: 0, salePrice: null, isOnSale: false };
  }

  const list = getListPriceFromProduct(product);
  const shelf = parsePriceInput(product.price);
  const explicitSale =
    parsePriceInput(product.salePrice) ?? parsePriceInput(product.sale_price);
  const onSaleFlag = Boolean(product.isOnSale);

  if (list != null && shelf != null && list > shelf) {
    return {
      price: shelf,
      originalPrice: list,
      salePrice: shelf,
      isOnSale: true,
    };
  }

  if (explicitSale != null && list != null && list > explicitSale) {
    return {
      price: explicitSale,
      originalPrice: list,
      salePrice: explicitSale,
      isOnSale: true,
    };
  }

  if (explicitSale != null && shelf != null && shelf > explicitSale) {
    return {
      price: explicitSale,
      originalPrice: shelf,
      salePrice: explicitSale,
      isOnSale: true,
    };
  }

  if (onSaleFlag && explicitSale != null && shelf != null && shelf > explicitSale) {
    return {
      price: explicitSale,
      originalPrice: shelf,
      salePrice: explicitSale,
      isOnSale: true,
    };
  }

  const base = list ?? shelf ?? 0;
  return {
    price: base,
    originalPrice: list ?? base,
    salePrice: explicitSale,
    isOnSale: false,
  };
}

/** Admin form: original + sale inputs from API product. */
export function pricesFromStoredProduct(product) {
  if (!product) return { originalPrice: '', salePrice: '' };
  const norm = normalizeProductPricesForDisplay(product);
  const onSale = norm.isOnSale && norm.originalPrice > norm.price;
  return {
    originalPrice: String(norm.originalPrice ?? ''),
    salePrice: onSale ? String(norm.price ?? '') : '',
  };
}

function listPriceAliases(listAmount) {
  if (listAmount == null || !Number.isFinite(listAmount)) return {};
  return {
    compareAtPrice: listAmount,
    compare_at_price: listAmount,
    listPrice: listAmount,
    list_price: listAmount,
    mrp: listAmount,
    regularPrice: listAmount,
  };
}

/** JSON PUT body — pricing fields only (no empty category/name). */
export function buildProductPricingPatchBody(originalPriceInput, salePriceInput, options = {}) {
  const pricing = buildProductPricingFields(originalPriceInput, salePriceInput, options);
  const body = {
    price: pricing.price,
    originalPrice: pricing.originalPrice,
    isOnSale: pricing.isOnSale,
  };
  if (pricing.salePrice != null) {
    body.salePrice = pricing.salePrice;
  } else {
    body.salePrice = null;
  }
  if (pricing.discount !== undefined) {
    body.discount = pricing.discount;
  }
  return body;
}

/**
 * Admin save payload: always persist list (real) + selling price.
 */
export function buildProductPricingFields(originalPriceInput, salePriceInput, options = {}) {
  const original = parsePriceInput(originalPriceInput);
  const sale = parsePriceInput(salePriceInput);
  const forceOnSale = Boolean(options.isOnSaleFlag);

  const hasValidSale =
    sale != null && original != null && original > 0 && sale < original;

  if (hasValidSale || (forceOnSale && sale != null && original != null && sale < original)) {
    const discount = Math.round(((original - sale) / original) * 100);
    return {
      price: sale,
      originalPrice: original,
      salePrice: sale,
      isOnSale: true,
      discount: Number.isFinite(discount) ? discount : undefined,
      ...listPriceAliases(original),
    };
  }

  const base = original ?? sale ?? 0;
  const list = original ?? base;
  return {
    price: base,
    originalPrice: list,
    salePrice: null,
    isOnSale: false,
    discount: undefined,
    ...listPriceAliases(list),
  };
}

/**
 * Multipart create: backend often stores `price` as list/MRP; sale goes in `salePrice`.
 * (JSON PUT after create aligns with edit — see syncProductPricingAfterCreate.)
 */
export function getCreateMultipartPricing(productData) {
  const list =
    parsePriceInput(productData?.originalPrice) ??
    parsePriceInput(productData?.price);
  const sale = parsePriceInput(productData?.salePrice);
  const priceIndicatesSale =
    sale != null && list != null && list > 0 && sale < list;
  const checkboxOnSale = Boolean(productData?.isOnSale);
  const isOnSale = checkboxOnSale || priceIndicatesSale;

  if (priceIndicatesSale) {
    return {
      price: list,
      originalPrice: list,
      salePrice: sale,
      isOnSale,
    };
  }

  const base = list ?? 0;
  return {
    price: base,
    originalPrice: base,
    salePrice: null,
    isOnSale: checkboxOnSale && isOnSale,
  };
}

/** Multipart create: only `originalPrice` (extra keys can 500 strict backends). */
export function appendListPriceFormFields(formData, listAmount) {
  const n =
    typeof listAmount === 'number'
      ? listAmount
      : parsePriceInput(listAmount);
  if (n == null || !Number.isFinite(n)) return;
  formData.append('originalPrice', String(n));
}
